import { useState, useEffect } from "react";
import { X, Download, Share, Plus } from "lucide-react";
import { tokens } from "@/design-system/tokens";

const DISMISS_KEY = "fretless_install_banner_dismissed";

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia("(display-mode: standalone)").matches
    || window.navigator.standalone === true;
};

const isMobileDevice = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isNarrow = window.innerWidth < 768;
  return isTouchDevice && isNarrow;
};

const getMobilePlatform = () => {
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
  if (isIOS && isSafari) return "ios";
  if (isAndroid) return "android";
  return null; // Not a recognized mobile platform
};

export function MobileInstallBanner() {
  const [visible, setVisible] = useState(false);
  const [showIOSSteps, setShowIOSSteps] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState(null);

  useEffect(() => {
    // Gate 1: Must be a mobile device (touch + narrow viewport)
    if (!isMobileDevice()) return;
    // Gate 2: Must not already be installed
    if (isStandalone()) return;
    // Gate 3: Must be a recognized mobile platform
    const plat = getMobilePlatform();
    if (!plat) return;
    // Gate 4: Must not have been dismissed
    try { if (localStorage.getItem(DISMISS_KEY)) return; } catch {}
    
    setPlatform(plat);
    setVisible(true);
  }, []);

  // Only capture beforeinstallprompt if banner is actually showing (Android mobile)
  useEffect(() => {
    if (!visible || platform !== "android") return;
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [visible, platform]);

  const handleDismiss = () => {
    setVisible(false);
    setShowIOSSteps(false);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch {}
  };

  const handleTap = async () => {
    if (platform === "android" && deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      handleDismiss();
    } else if (platform === "ios") {
      setShowIOSSteps(true);
    }
  };

  if (!visible) return null;

  // iOS step-by-step overlay
  if (showIOSSteps) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 200,
          backgroundColor: "rgba(0,0,0,0.9)",
          backdropFilter: "blur(12px)",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          animation: "fadeIn 300ms ease-out"
        }}
        data-testid="ios-install-overlay"
      >
        <div style={{
          width: "100%",
          maxWidth: "448px",
          backgroundColor: tokens.color.cardBg,
          border: `1px solid ${tokens.color.innerBorder}`,
          borderRadius: "32px 32px 0 0",
          padding: `${tokens.space.xxl}px`,
          position: "relative"
        }}>
          <button
            onClick={handleDismiss}
            style={{
              position: "absolute",
              top: `${tokens.space.lg}px`,
              right: `${tokens.space.lg}px`,
              color: tokens.color.textMuted,
              background: "none",
              border: "none",
              cursor: "pointer",
              transition: "color 150ms ease"
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = tokens.color.text}
            onMouseLeave={(e) => e.currentTarget.style.color = tokens.color.textMuted}
            aria-label="Close"
          >
            <X style={{ height: "20px", width: "20px" }} />
          </button>
          <h2 style={{ fontSize: "20px", fontWeight: 700, color: tokens.color.text, marginBottom: `${tokens.space.xs}px`, fontFamily: "Fraunces, serif", margin: 0 }}>Add Fretless to Home Screen</h2>
          <p style={{ color: tokens.color.textMuted, fontSize: "12px", marginBottom: "20px", margin: 0, marginTop: `${tokens.space.md}px` }}>Use it like an app — no app store needed</p>
          <div style={{ borderTop: `1px solid ${tokens.color.innerBorder}` }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: `${tokens.space.xl}px`, paddingTop: `${tokens.space.md}px`, paddingBottom: `${tokens.space.md}px` }}>
              <div style={{ flexShrink: 0, width: "32px", height: "32px", borderRadius: "50%", backgroundColor: "rgba(245,137,31,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 700, color: tokens.color.orange }}>1</div>
              <div style={{ display: "flex", alignItems: "center", gap: `${tokens.space.md}px`, fontSize: "12px", color: tokens.color.textMuted }}>
                <Share style={{ height: "16px", width: "16px", color: tokens.color.orange }} />
                <span>Tap the Share icon at the bottom of Safari</span>
              </div>
            </div>
            <div style={{ borderTop: `1px solid ${tokens.color.innerBorder}` }} />
            <div style={{ display: "flex", alignItems: "flex-start", gap: `${tokens.space.xl}px`, paddingTop: `${tokens.space.md}px`, paddingBottom: `${tokens.space.md}px` }}>
              <div style={{ flexShrink: 0, width: "32px", height: "32px", borderRadius: "50%", backgroundColor: "rgba(245,137,31,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 700, color: tokens.color.orange }}>2</div>
              <div style={{ display: "flex", alignItems: "center", gap: `${tokens.space.md}px`, fontSize: "12px", color: tokens.color.textMuted }}>
                <Plus style={{ height: "16px", width: "16px", color: tokens.color.orange }} />
                <span>Scroll down and tap "Add to Home Screen"</span>
              </div>
            </div>
            <div style={{ borderTop: `1px solid ${tokens.color.innerBorder}` }} />
            <div style={{ display: "flex", alignItems: "flex-start", gap: `${tokens.space.xl}px`, paddingTop: `${tokens.space.md}px`, paddingBottom: `${tokens.space.md}px` }}>
              <div style={{ flexShrink: 0, width: "32px", height: "32px", borderRadius: "50%", backgroundColor: "rgba(245,137,31,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 700, color: tokens.color.orange }}>3</div>
              <div style={{ display: "flex", alignItems: "center", gap: `${tokens.space.md}px`, fontSize: "12px", color: tokens.color.textMuted }}>
                <Download style={{ height: "16px", width: "16px", color: tokens.color.orange }} />
                <span>Tap "Add" in the top right</span>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: `${tokens.space.md}px`, animation: "bounce 1s infinite" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: tokens.color.orange }}>
              <path d="M12 4v14m0 0l-5-5m5 5l5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <button
            onClick={handleDismiss}
            style={{
              width: "100%",
              marginTop: `${tokens.space.lg}px`,
              padding: `12px 16px`,
              borderRadius: tokens.radius.lg,
              backgroundColor: tokens.color.orange,
              color: "#faf8f5",
              fontWeight: 700,
              fontSize: "12px",
              border: "none",
              cursor: "pointer",
              transition: "background-color 150ms ease"
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = "0.9"}
            onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}
            data-testid="ios-install-got-it"
          >
            Got it
          </button>
        </div>
        <style>{`
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        `}</style>
      </div>
    );
  }

  // Sticky bottom banner — md:hidden ensures CSS-level hiding above 768px too
  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 150,
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        display: window.innerWidth >= 768 ? "none" : "block"
      }}
      data-testid="mobile-install-banner"
    >
      <div style={{
        backgroundColor: tokens.color.orange,
        margin: `${tokens.space.md}px`,
        marginBottom: `${tokens.space.md}px`,
        borderRadius: tokens.radius.lg,
        padding: `${tokens.space.md}px ${tokens.space.lg}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxShadow: tokens.shadow.card
      }}>
        <button
          onClick={handleTap}
          style={{
            display: "flex",
            alignItems: "center",
            gap: `${tokens.space.md}px`,
            flex: 1,
            minWidth: 0,
            background: "none",
            border: "none",
            cursor: "pointer"
          }}
          data-testid="install-banner-tap"
        >
          <div style={{
            width: "36px",
            height: "36px",
            borderRadius: tokens.radius.md,
            backgroundColor: "rgba(0,0,0,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
          }}>
            <Download style={{ height: "20px", width: "20px", color: "#faf8f5" }} />
          </div>
          <div style={{ textAlign: "left", minWidth: 0 }}>
            <p style={{ fontSize: "12px", fontWeight: 700, color: "#faf8f5", lineHeight: 1.2, margin: 0 }}>Install Fretless</p>
            <p style={{ fontSize: "10px", color: "rgba(250,248,245,0.7)", lineHeight: 1.2, margin: 0 }}>Add to your home screen</p>
          </div>
        </button>
        <button
          onClick={handleDismiss}
          style={{
            marginLeft: `${tokens.space.md}px`,
            padding: "6px",
            borderRadius: "50%",
            background: "none",
            border: "none",
            cursor: "pointer",
            transition: "background-color 150ms ease",
            flexShrink: 0
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.1)"}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
          aria-label="Dismiss"
          data-testid="install-banner-dismiss"
        >
          <X style={{ height: "16px", width: "16px", color: "#faf8f5" }} />
        </button>
      </div>
    </div>
  );
}

// Hook for "Install app" in More drawer — only true on mobile, not installed
export function useCanInstall() {
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    // Must be mobile device (touch + narrow) AND not standalone
    if (!isMobileDevice() || isStandalone()) return;
    const plat = getMobilePlatform();
    if (plat) setCanInstall(true);
  }, []);

  return canInstall;
}
